import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useRef } from "react";
import { type StudioNotification, api, notificationsQuery, overviewQuery } from "../lib/api";
import { initials, timeAgo } from "../lib/format";
import { Icon } from "./Icons";
import { TourHelp } from "./Tour";

/** Whether the notification center is open; the Shell provides it, every page's top bar shows the bell. */
export const BellContext = createContext<{ open: boolean; toggle: () => void } | null>(null);

/** The bell at the end of a page's top bar (on phones it's in the top bar beside Menu instead). */
export function PageBell() {
  const bell = useContext(BellContext);
  if (!bell) return null;
  return (
    <span className="page-bell">
      <TourHelp />
      <NotificationBell open={bell.open} onToggle={bell.toggle} />
    </span>
  );
}

/** The bell, with how many notifications are unread. */
export function NotificationBell({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const unread = useQuery(overviewQuery).data?.notifications ?? 0;
  return (
    <button
      type="button"
      className="bell"
      data-tour="bell"
      aria-expanded={open}
      aria-haspopup="dialog"
      onClick={onToggle}
      aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
      title="Notifications"
    >
      <Icon name="bell" />
      {unread > 0 && <span className="bell-count">{unread > 99 ? "99+" : unread}</span>}
    </button>
  );
}

/**
 * The notification center: what concerns you, newest first. Opening one marks it read and goes there.
 * Like the emails, it says who did what and where, never what a comment says.
 */
export function NotificationPanel({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const router = useRouter();
  const q = useQuery({ ...notificationsQuery, refetchInterval: 30_000 });
  const panel = useRef<HTMLDivElement>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: notificationsQuery.queryKey });
    void qc.invalidateQueries({ queryKey: overviewQuery.queryKey });
  };
  const read = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => api("/studio/me/notifications/read", { method: "POST", json: body }),
    onSuccess: refresh,
  });

  // Focus moves into the panel; Escape or a click outside closes it.
  useEffect(() => {
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element;
      if (!panel.current?.contains(t) && !t.closest(".bell")) onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [onClose]);

  const open = (n: StudioNotification) => {
    if (!n.readAt) read.mutate({ ids: [n.id] });
    onClose();
    router.history.push(n.path);
  };
  const list = q.data?.notifications ?? [];
  const unread = list.filter((n) => !n.readAt).length;

  return (
    <div className="notif-panel" role="dialog" aria-label="Notifications" ref={panel} tabIndex={-1}>
      <header className="notif-head">
        <h2>Notifications</h2>
        <span className="notif-head-actions">
          {unread > 0 && (
            <button type="button" className="link muted" onClick={() => read.mutate({ all: true })} disabled={read.isPending}>
              Mark all read
            </button>
          )}
          {/* On phones the panel fills the screen, so it needs its own way out. */}
          <button type="button" className="notif-close" aria-label="Close notifications" onClick={onClose}>
            ✕
          </button>
        </span>
      </header>
      <div className="notif-body">
        {q.isPending && <div className="skeleton-list" aria-busy="true" />}
        {q.error && <p className="alert">{q.error.message}</p>}
        {q.data && !list.length && <p className="muted small notif-empty">Nothing yet. Mentions, replies, client notes and reviews show up here.</p>}
        <ul className="notif-list">
          {list.map((n) => (
            <li key={n.id}>
              <button type="button" className={`notif ${n.readAt ? "" : "unread"}`} onClick={() => open(n)}>
                {n.actor?.avatarUrl ? (
                  <img src={n.actor.avatarUrl} alt="" className="av" referrerPolicy="no-referrer" />
                ) : (
                  <span className="av">{n.actor?.name ? initials(n.actor.name) : <Icon name={n.kind === "upload_failed" ? "media" : "bell"} />}</span>
                )}
                <span className="notif-text">
                  <span>
                    {!n.readAt && <span className="sr-only">Unread: </span>}
                    {n.text}
                  </span>
                  <small className="muted">
                    {n.actor?.client ? "Client · " : ""}
                    {timeAgo(n.createdAt)}
                  </small>
                </span>
                {!n.readAt && <span className="unread-dot" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <footer className="notif-foot">
        <Link to="/account" hash="notifications" className="link muted" onClick={onClose}>
          Choose what's emailed, and the daily summary
        </Link>
      </footer>
    </div>
  );
}
